// src/components/events/OrganizerInvitation.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, User, X, Mail, Send, CheckCircle, Clock, XCircle } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { invitationsService } from '../../services/invitationsService';
import { useAuth } from '../../contexts/AuthContext';

export const OrganizerInvitation = ({ bookData, bookId, onInvitationSent }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadPendingInvitations();
  }, [bookId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      searchUsers();
    } else {
      setUsers([]);
    }
  }, [searchTerm]);

  const loadPendingInvitations = async () => {
    try {
      const invitations = await invitationsService.getBookInvitations(bookId);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const searchUsers = async () => {
    setLoading(true);
    try {
      const searchLower = searchTerm.toLowerCase();
      const usersRef = collection(db, 'pb-users');
      
      // Search by email
      const emailQuery = query(
        usersRef,
        where('email', '>=', searchLower),
        where('email', '<=', searchLower + '\uf8ff'),
        limit(10)
      );
      
      const snapshot = await getDocs(emailQuery);
      
      // Filter out current organizers and pending invitations
      const existingOrganizerIds = (bookData.organizers || []).map(org => org.userId || org.id);
      const pendingInviteeIds = pendingInvitations.map(inv => inv.inviteeUserId);
      const excludeIds = [...existingOrganizerIds, ...pendingInviteeIds, user.uid];
      
      const usersList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u => !excludeIds.includes(u.id));

      setUsers(usersList);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendInvitation = async (invitee) => {
    setSending(true);
    try {
      await invitationsService.sendOrganizerInvitation(
        bookId,
        invitee.email,
        invitee.id,
        user,
        bookData
      );
      
      setSearchTerm('');
      setShowDropdown(false);
      await loadPendingInvitations();
      onInvitationSent?.();
      
      alert(`Invitation sent to ${invitee.displayName || invitee.email}!`);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const cancelInvitation = async (invitationId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    
    try {
      await invitationsService.cancelInvitation(invitationId);
      await loadPendingInvitations();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Failed to cancel invitation.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Invite Organizer by Email
        </label>
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length >= 3 && setShowDropdown(true)}
              placeholder="Search by email address..."
              className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
              </div>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && users.length > 0 && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {u.photoURL ? (
                        <img src={u.photoURL} alt={u.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{u.displayName || 'User'}</p>
                      <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {u.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => sendInvitation(u)}
                    disabled={sending}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <Send className="h-4 w-4" />
                    Invite
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* No results */}
          {showDropdown && searchTerm.length >= 3 && users.length === 0 && !loading && (
            <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center text-gray-500">
              No users found with email "{searchTerm}"
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Type at least 3 characters to search for users by email
        </p>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Pending Invitations</h4>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {invitation.inviteeEmail}
                    </p>
                    <p className="text-xs text-gray-600">
                      Invited {new Date(invitation.createdAt?.toDate()).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => cancelInvitation(invitation.id)}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};